import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';
import { Box } from '@mui/material';
import { green, orange, red } from '@mui/material/colors';
import { useQueries } from '@tanstack/react-query';
import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';

import { getAdvisorNumberListApi, getTechNumberListApi } from 'api/tickets';
import { uploadUserColumnPreferences } from 'api/user';
import DotsLoader from 'components/DotsLoader/DotsLoader';
import type { MuiFormSelectOptionT } from 'components/Form/MuiFormSelect/MuiFormSelect';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketAssigneeE, TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { isScheduleWorkType } from 'declarations/enums/tickets';
import type { Column, NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { convertNumbersToSelectOptions } from 'helpers/selectOptions';
import { useFetchUserColumnPreferences } from 'hooks/api/useUser';
import {
  SCHEDULE_EXCLUDED_COLUMN_IDS,
  TICKET_EXCLUDED_COLUMN_IDS,
  getColumnsForSection,
  getStorageKeyForSection,
} from 'pages/TicketsPage/components/ColumnPickerSettingsPopup/components/ColumnPickerSettings/mockedColumns';
import { getLastMessageDisplayText } from 'pages/TicketsPage/helpers';
import type { ChangeTicketsListFormValues } from 'pages/TicketsPage/hooks/useTicketsPage';
import { MessageWrapStyled } from 'pages/TicketsPage/TicketsPage.styled';

import { TicketsTable } from '../Table';
import useChangeTicketDetailsForm from '../TicketDetailsPopup/hooks/useChangeTicketDetailsForm';
import useTicketsRefinement from '../TicketsRefinement/hooks/useTicketsRefinement';

import { useTicketsListView } from './hooks/useTicketsListView';
import { ContentStyled, FormStyled, ListViewStyled } from './TicketsListView.styled';

/** Claim status IDs that require OEM assignee. 1=Pending, 7=ManualReview. */
const OEM_RESERVED_CLAIM_STATUS_IDS = [1, 7];

export type TicketsListViewPropsT = {
  ticketsCount: number;
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  isLoadingTickets: boolean;
  onSelectTicket: (id: string, payload: { roNumber: string; lineNumber: string | null }) => NoopT;
  isViewed?: boolean;
  columns: Column[];
  onSaveChanges: (data: Record<string, ChangeTicketsListFormValues>) => Promise<void>;
  formMethods: UseFormReturn<Record<string, ChangeTicketsListFormValues>>;
};

type ColumnVisibilityMap = { [key: string]: boolean };

const TicketsListView: FC<TicketsListViewPropsT> = ({
  ticketsPages,
  ticketsCount,
  isLoadingTickets,
  onSelectTicket,
  columns,
  onSaveChanges,
  formMethods,
}) => {
  /**
   * Hooks
   */
  const { isMobile } = useResponsive();
  const {
    handleTicketSelection,
    selectedTickets,
    setSelectedTickets,
    selectedTicketsRooftops,
    setSelectedTicketsRooftops,
  } = useTicketsListView({ columns });
  const {
    isFetchingNextTicketsPage,
    isFetchingTickets,
    isSuccessfullyFetchedTickets,
    setSelectedTicketsRooftopIds,
    isEditMode,
    roofTopsList,
    getSearchParam,
    activeTab,
  } = useTickets();
  const { handleSelectAllTicketsClick, handleUpdateSortingClick } = useTicketsRefinement();
  const {
    claimStatusesOptions,
    isLoadingClaimStatusesOptions,
    isLoadingTicketPrioritiesOptions,
    isLoadingTicketSubcategoriesOptions,
    ticketPrioritiesOptions,
    ticketSubcategoriesOptions,
  } = useChangeTicketDetailsForm();

  const sortParameter = getSearchParam(TicketsRefinementKeysE.Parameter) ?? '';
  const sortOrder = (getSearchParam(TicketsRefinementKeysE.Order) as 'asc' | 'desc') ?? 'asc';
  const onRequestSort = (param: string): (() => void) =>
    handleUpdateSortingClick(TicketsRefinementKeysE.Parameter, param);

  // /**
  //  * Variables
  //  */
  const shouldLoadMore = isFetchingTickets && isFetchingNextTicketsPage;
  const isScheduleSection = activeTab === ActiveTabTypeE.Schedule;
  const sectionStorageKey = getStorageKeyForSection(activeTab);
  const sectionColumns = getColumnsForSection(activeTab);

  const userColumnPreferences = useFetchUserColumnPreferences({
    onSuccess: (data): void => {
      localStorage.setItem('visibleColumns', data);
    },
    onError: (err): void => {
      if (err && err.data?.detail?.startsWith('The specified key does not exist.')) {
        const defaultColumnVisibility: ColumnVisibilityMap = {};
        columns.forEach((column): void => {
          defaultColumnVisibility[column.id] = true;
        });

        uploadUserColumnPreferences({ RawData: JSON.stringify(columns) });
      }
    },
  });

  const { handleSubmit } = formMethods;
  const watchedFormValues = formMethods.watch();

  useEffect(() => {
    selectedTickets.forEach(ticketId => {
      const ticketFormValues =
        watchedFormValues?.[`[${ticketId}]`] ?? watchedFormValues?.[ticketId];
      const claimStatusValue = Number(
        ticketFormValues?.claimStatusId ??
          ticketsPages?.flatMap(p => p.data).find(t => t?.id === ticketId)?.claimStatusId ??
          0
      );
      if (OEM_RESERVED_CLAIM_STATUS_IDS.includes(claimStatusValue)) {
        formMethods.setValue(`[${ticketId}].assigneeName`, 'OEM', {
          shouldDirty: true,
          shouldTouch: true,
        });
      } else {
        const currentAssignee =
          ticketFormValues?.assigneeName ??
          ticketsPages?.flatMap(p => p.data).find(t => t?.id === ticketId)?.assigneeName ??
          '';
        if (currentAssignee.trim().toUpperCase() === 'OEM') {
          formMethods.setValue(`[${ticketId}].assigneeName`, TicketAssigneeE.WarrCloud, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      }
    });
  }, [selectedTickets, ticketsPages, watchedFormValues, formMethods]);

  const storedVisibleColumnsJSON =
    userColumnPreferences.data ??
    localStorage.getItem(sectionStorageKey) ??
    (isScheduleSection ? null : localStorage.getItem('visibleColumns'));
  const storedVisibleColumns = useMemo(
    () => (storedVisibleColumnsJSON ? JSON.parse(storedVisibleColumnsJSON) : []),
    [storedVisibleColumnsJSON]
  );

  /** Migrate saved column labels to current header labels (RO number→RO, etc.) */
  const COLUMN_LABEL_MIGRATION: Record<string, string> = {
    'RO number': 'RO',
    'Line number': 'Line',
    Priority: 'P',
    'Age (days)': 'Age',
    'Claim Age (days)': 'Claim Age',
    'Assigned to:': 'Assignee',
    'Advisor number': 'Advisor',
    'Tech number': 'Tech',
    'Account Number': 'Account',
  };
  /** Merge stored columns with defaults: apply migration, then append any default columns missing from stored (e.g. Audit Trail) */
  const columnOrderForTable: Column[] = ((): Column[] => {
    const defaultCols = sectionColumns;
    if (
      !Array.isArray(storedVisibleColumns) ||
      storedVisibleColumns.length === 0 ||
      typeof storedVisibleColumns[0] !== 'object' ||
      storedVisibleColumns[0] === null ||
      !('label' in (storedVisibleColumns[0] as Column))
    ) {
      return defaultCols;
    }
    const migrated = (storedVisibleColumns as Column[]).map(col => ({
      ...col,
      label: COLUMN_LABEL_MIGRATION[col.label] ?? col.label,
    }));
    const storedIds = new Set(migrated.map((c): string => c.id));
    const missing = defaultCols.filter(col => !storedIds.has(col.id));
    let result = missing.length > 0 ? [...migrated, ...missing] : migrated;
    if (isScheduleSection) {
      result = result.filter(col => !SCHEDULE_EXCLUDED_COLUMN_IDS.includes(col.id));
    } else {
      result = result.filter(col => !TICKET_EXCLUDED_COLUMN_IDS.includes(col.id));
    }
    return result;
  })();

  /** Persist merged columns when we added missing defaults (e.g. Audit Trail) so they appear on next load */
  const [hasPersistedMerge, setHasPersistedMerge] = useState(false);
  useEffect(() => {
    const storedCount = Array.isArray(storedVisibleColumns) ? storedVisibleColumns.length : 0;
    if (storedCount > 0 && columnOrderForTable.length > storedCount && !hasPersistedMerge) {
      setHasPersistedMerge(true);
      localStorage.setItem(sectionStorageKey, JSON.stringify(columnOrderForTable));
      if (!isScheduleSection) {
        uploadUserColumnPreferences({ RawData: JSON.stringify(columnOrderForTable) });
      }
    }
  }, [
    columnOrderForTable,
    storedVisibleColumns,
    hasPersistedMerge,
    sectionStorageKey,
    isScheduleSection,
  ]);

  const getAssigneeOptions = (roofTopId: string): SelectOptionT[] => {
    const targetRooftop = roofTopsList?.find(mappedRooftop => mappedRooftop.id === roofTopId);
    const dealerLabel = targetRooftop?.name ?? 'Chirp';
    const dealerValue = targetRooftop?.name ?? TicketAssigneeE.Chirp;
    return [
      { label: dealerLabel, value: dealerValue },
      { label: TicketAssigneeE.WarrCloud, value: TicketAssigneeE.WarrCloud },
      {
        label: 'OEM',
        value: 'OEM',
        disabled: true,
        disabledTooltip:
          'Selecting the correct claim status will automatically assign this ticket to the OEM.',
      },
    ];
  };

  /** Unique roofTopIds from selected tickets (for advisor/tech fetch when in edit mode) */
  const editModeRoofTopIds = useMemo((): string[] => {
    if (!isEditMode || selectedTickets.length === 0 || !ticketsPages) return [];
    const allTickets = ticketsPages.flatMap(page => page.data ?? []);
    const ids = new Set<string>();
    selectedTickets.forEach(ticketId => {
      const ticket = allTickets.find(t => t.id === ticketId);
      if (ticket?.roofTop?.id) ids.add(ticket.roofTop.id);
    });
    return Array.from(ids);
  }, [isEditMode, selectedTickets, ticketsPages]);

  const advisorQueries = useQueries({
    queries: editModeRoofTopIds.map(roofTopId => ({
      queryKey: [RequestQueryKeysE.FetchAdvisorNumberList, roofTopId],
      queryFn: (): Promise<Parameters<typeof convertNumbersToSelectOptions>[0]> =>
        getAdvisorNumberListApi(roofTopId),
      enabled: editModeRoofTopIds.length > 0,
    })),
  });

  const techQueries = useQueries({
    queries: editModeRoofTopIds.map(roofTopId => ({
      queryKey: [RequestQueryKeysE.FetchTechNumberList, roofTopId],
      queryFn: (): Promise<Parameters<typeof convertNumbersToSelectOptions>[0]> =>
        getTechNumberListApi(roofTopId),
      enabled: editModeRoofTopIds.length > 0,
    })),
  });

  const advisorOptionsByRoofTop = useMemo((): Record<string, SelectOptionT[]> => {
    const map: Record<string, SelectOptionT[]> = {};
    editModeRoofTopIds.forEach((roofTopId, i) => {
      const result = advisorQueries[i]?.data;
      map[roofTopId] = result ? convertNumbersToSelectOptions(result) : [];
    });
    return map;
  }, [editModeRoofTopIds, advisorQueries]);

  const techOptionsByRoofTop = useMemo((): Record<string, SelectOptionT[]> => {
    const map: Record<string, SelectOptionT[]> = {};
    editModeRoofTopIds.forEach((roofTopId, i) => {
      const result = techQueries[i]?.data;
      map[roofTopId] = result ? convertNumbersToSelectOptions(result) : [];
    });
    return map;
  }, [editModeRoofTopIds, techQueries]);

  const getAdvisorOptions = (roofTopId: string): SelectOptionT[] =>
    advisorOptionsByRoofTop[roofTopId] ?? [];
  const getTechOptions = (roofTopId: string): SelectOptionT[] =>
    techOptionsByRoofTop[roofTopId] ?? [];

  /** Priority options with icons (same as ticket view / new ticket modal) for table row dropdown */
  const ticketPrioritiesOptionsWithIcon: MuiFormSelectOptionT[] = useMemo(() => {
    const labelToIconAndColor: Record<
      string,
      { icon: React.ReactNode; sxProps: { color: string } }
    > = {
      Critical: { icon: <KeyboardDoubleArrowUp key="critical" />, sxProps: { color: red[900] } },
      High: { icon: <KeyboardArrowUp key="high" />, sxProps: { color: red[500] } },
      Medium: { icon: <KeyboardArrowDown key="medium" />, sxProps: { color: orange[500] } },
      Low: { icon: <KeyboardDoubleArrowDown key="low" />, sxProps: { color: green[500] } },
    };
    return ticketPrioritiesOptions.map(opt => {
      const mapped = labelToIconAndColor[opt.label];
      return {
        ...opt,
        icon: mapped?.icon ?? opt.label,
        sxProps: mapped?.sxProps,
      };
    });
  }, [ticketPrioritiesOptions]);

  const normalizeString = (str: string): string => str?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  const handleRowClick =
    (id: string, roofTopId: string, payload: { roNumber: string; lineNumber: string | null }) =>
    (): void => {
      onSelectTicket(id, payload)();
      if (!selectedTicketsRooftops.includes(roofTopId) && selectedTickets.length <= 1) {
        setSelectedTicketsRooftopIds([roofTopId]);
        setSelectedTicketsRooftops([roofTopId]);
      } else if (!selectedTicketsRooftops.includes(roofTopId) && selectedTickets.length > 1) {
        setSelectedTicketsRooftops([...selectedTicketsRooftops, roofTopId]);
        setSelectedTicketsRooftopIds([...selectedTicketsRooftops, roofTopId]);
      }
    };

  // /**
  //  * Effects
  //  */
  useEffect(() => {
    if (!localStorage.getItem(sectionStorageKey)) {
      localStorage.setItem(sectionStorageKey, JSON.stringify(sectionColumns));
    }
  }, [sectionStorageKey, sectionColumns]);

  useEffect(() => {
    const findOptionIdByName = (
      options: { label: string; value: string | number }[],
      name: string | null
    ): string | number | null =>
      options.find(
        (option): boolean => normalizeString(option.label) === normalizeString(name || '')
      )?.value || null;

    formMethods.reset();

    selectedTickets.forEach(ticketId => {
      const ticket = ticketsPages
        ?.flatMap(page => page.data)
        .find(ticket => ticket.id === ticketId);

      if (ticket) {
        const subCategoryName = ticket.subCategory ?? ticket.requestSubCategoryName ?? null;
        const subCategoryId: number | null =
          (ticket.subCategoryId as number | null) ??
          ticket.requestSubCategoryId ??
          (findOptionIdByName(ticketSubcategoriesOptions, subCategoryName) as number | null);
        const priorityId = ticket.ticketPriorityId
          ? ticket.ticketPriorityId
          : Number(
              ticketPrioritiesOptions?.find(option => option.label === ticket.ticketPriority)?.value
            ) || 1;

        const parsedLastMsg = getLastMessageDisplayText(ticket.lastMessage);

        formMethods.setValue(`[${ticketId}].claimStatusId`, ticket.claimStatusId ?? 1);
        formMethods.setValue(`[${ticketId}].ticketPriorityId`, priorityId);
        formMethods.setValue(`[${ticketId}].subCategoryId`, subCategoryId || null);
        const workTypeValue = ticket.workTypeId ?? ticket.workType;
        formMethods.setValue(
          `[${ticketId}].workTypeId`,
          isScheduleWorkType(workTypeValue) ? workTypeValue : null
        );
        const rooftopForAssignee = roofTopsList?.find(r => r.id === ticket.roofTop.id);
        const assigneeDisplayValue =
          ticket.assigneeName === TicketAssigneeE.Chirp && rooftopForAssignee
            ? rooftopForAssignee.name
            : ticket.assigneeName ?? '';
        formMethods.setValue(`[${ticketId}].assigneeName`, assigneeDisplayValue);
        formMethods.setValue(`[${ticketId}].amount`, String(ticket.amount ?? 0));
        formMethods.setValue(
          `[${ticketId}].dmsScheduleAccountNumber`,
          ticket.dmsScheduleAccountNumber != null ? String(ticket.dmsScheduleAccountNumber) : null
        );
        formMethods.setValue(`[${ticketId}].advisorNumber`, ticket.advisorNumber ?? '');
        formMethods.setValue(`[${ticketId}].techNumber`, ticket.techNumber ?? '');
        formMethods.setValue(`[${ticketId}].text`, parsedLastMsg);
      }
    });
  }, [
    selectedTickets,
    ticketsPages,
    formMethods,
    ticketSubcategoriesOptions,
    ticketPrioritiesOptions,
    roofTopsList,
  ]);

  if (isSuccessfullyFetchedTickets && ticketsCount === 0) {
    return (
      <ListViewStyled>
        <FormProvider {...formMethods}>
          <FormStyled role="form" onSubmit={handleSubmit(onSaveChanges)}>
            <ContentStyled data-testid="tickets-list">
              <TicketsTable
                ticketsPages={[]}
                columnOrder={columnOrderForTable}
                selectedRows={selectedTickets}
                setSelectedRows={setSelectedTickets}
                handleCheckClick={handleTicketSelection}
                onRowClick={handleRowClick}
                onSelectAllClick={handleSelectAllTicketsClick}
                sortParameter={sortParameter}
                sortOrder={sortOrder}
                onRequestSort={onRequestSort}
                selectedTickets={selectedTickets}
                setSelectedTickets={setSelectedTickets}
                loading={false}
                isFetchingNextTicketsPage={false}
                isEditMode={isEditMode}
                formMethods={formMethods}
                ticketPrioritiesOptions={ticketPrioritiesOptions}
                ticketPrioritiesOptionsWithIcon={ticketPrioritiesOptionsWithIcon}
                ticketSubcategoriesOptions={ticketSubcategoriesOptions}
                claimStatusesOptions={claimStatusesOptions}
                getAssigneeOptions={getAssigneeOptions}
                getAdvisorOptions={getAdvisorOptions}
                getTechOptions={getTechOptions}
                isLoadingTicketPrioritiesOptions={isLoadingTicketPrioritiesOptions}
                isLoadingTicketSubcategoriesOptions={isLoadingTicketSubcategoriesOptions}
                isLoadingClaimStatusesOptions={isLoadingClaimStatusesOptions}
                isMobile={isMobile}
                isScheduleSection={activeTab === ActiveTabTypeE.Schedule}
              />
              <MessageWrapStyled>
                <Typography variant="h2" color="darkBlue" align="center">
                  Tickets not found
                </Typography>
              </MessageWrapStyled>
            </ContentStyled>
          </FormStyled>
        </FormProvider>
      </ListViewStyled>
    );
  }
  return (
    <ListViewStyled>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
        <FormProvider {...formMethods}>
          <FormStyled role="form" onSubmit={handleSubmit(onSaveChanges)}>
            <ContentStyled>
              <TicketsTable
                bottomSlot={shouldLoadMore ? <DotsLoader /> : null}
                ticketsPages={ticketsPages}
                columnOrder={columnOrderForTable}
                selectedRows={selectedTickets}
                setSelectedRows={setSelectedTickets}
                handleCheckClick={handleTicketSelection}
                onRowClick={handleRowClick}
                onSelectAllClick={handleSelectAllTicketsClick}
                sortParameter={sortParameter}
                sortOrder={sortOrder}
                onRequestSort={onRequestSort}
                selectedTickets={selectedTickets}
                setSelectedTickets={setSelectedTickets}
                loading={isLoadingTickets}
                isFetchingNextTicketsPage={isFetchingNextTicketsPage ?? false}
                isEditMode={isEditMode}
                formMethods={formMethods}
                ticketPrioritiesOptions={ticketPrioritiesOptions}
                ticketPrioritiesOptionsWithIcon={ticketPrioritiesOptionsWithIcon}
                ticketSubcategoriesOptions={ticketSubcategoriesOptions}
                claimStatusesOptions={claimStatusesOptions}
                getAssigneeOptions={getAssigneeOptions}
                getAdvisorOptions={getAdvisorOptions}
                getTechOptions={getTechOptions}
                isLoadingTicketPrioritiesOptions={isLoadingTicketPrioritiesOptions}
                isLoadingTicketSubcategoriesOptions={isLoadingTicketSubcategoriesOptions}
                isLoadingClaimStatusesOptions={isLoadingClaimStatusesOptions}
                isMobile={isMobile}
                isScheduleSection={activeTab === ActiveTabTypeE.Schedule}
              />
            </ContentStyled>
          </FormStyled>
        </FormProvider>
      </Box>
    </ListViewStyled>
  );
};

export default TicketsListView;
